import { PlanRow } from '#@itinerary/planner/days-overview.jsx';
import { PlanDetail } from '#@itinerary/planner/detail.jsx';
import { AddRowButton } from '#@itinerary/planner/row.jsx';
import { useTemplates } from '#@itinerary/planner/use-templates.js';
import { LoadingIndicator, LoadingSkeleton } from '#lib/components/loading.jsx';
import Input from '#lib/components/input.jsx';
import { useEffect, useState } from 'react';
import PopUp from '#components/popup.jsx';
import { cn } from '#lib/helpers/class.js';
import ManageControls from '#components/manage-controls.jsx';
import { DetailButtons } from '#components/control-buttons.jsx';
import { useGetTransferTypes } from '#@itinerary/planner/use-get-transfer-types.js';
import { newId, newIdPrefix } from '#helpers/helpers.js';
import { filterTemplates } from './helpers.js';
import useAuth from '#lib/hooks/use-auth.jsx';
import toast from 'react-hot-toast';
import { arrayMove } from '#lib/utils/array.js';

export default function Templates() {
    const { templates, addTemplate, destroyTemplate, updateTemplate, isLoading } = useTemplates();

    const popUpOptions = ['accommodation', 'activity', 'transfer', 'custom'];
    const [usableTemplates, setUsableTemplates] = useState(templates);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [selectedRoomId, setSelectedRoomId] = useState(null);
    const [searchValue, setSearchValue] = useState('');
    const [showPopUp, setShowPopUp] = useState(false);
    const { transferTypes } = useGetTransferTypes();
    const filteredTemplates = filterTemplates(usableTemplates, searchValue);
    const selectedTemplate = filteredTemplates?.find((el) => el.id === selectedTemplateId);

    useEffect(() => {
        if (templates) setUsableTemplates(templates);
    }, [templates]);

    const addNewTemplate = (type) => {
        setSearchValue('');
        const updated = structuredClone(usableTemplates);
        const id = newId();
        updated.push({ type, title: '', name: '', rooms: [], gallery: [], id, isBlocked: false });
        //setSelectedTemplateIndex(updated.length - 1);
        setSelectedTemplateId(id);
        setUsableTemplates(updated);
    };

    const handleSaveTemplate = () => {
        const templates = structuredClone(usableTemplates);
        const template = templates?.find((el) => el.id === selectedTemplateId);
        if (template.gallery.some((i) => i.isUploading)) {
            toast.error(_t.crud.itinerary.headers.waitMediaLoaded());
            return;
        }
        template.id.startsWith(newIdPrefix) ? addTemplate.mutate(template) : updateTemplate.mutate(template);
    };

    const handleChange = (event, id) => {
        const { name, value } = event.target;

        setUsableTemplates((prev) => {
            const templates = structuredClone(prev);
            const template = templates?.find((el) => el.id === selectedTemplateId);

            //DATI PLAN
            if (!id) template[name] = value;

            //DATI ROOMS
            if (id) {
                const room = template.rooms.find((room) => room.id === id);
                room[name] = value;
            }
            return templates;
        });
    };

    const handleDelete = (template, index) => {
        const confirm = window.confirm(_t.edit.confirmDeleteTemplate());
        if (!confirm) return;

        if (template.id.startsWith('NEW-')) {
            const filteredTemplates = usableTemplates.filter((ut) => ut.id !== template.id);
            setUsableTemplates(filteredTemplates);
        } else destroyTemplate.mutate({ type: template.type, id: template.id });

        //if (index < selectedTemplateIndex) setSelectedTemplateIndex((prev) => prev - 1);
        //if (index === selectedTemplateIndex && index === usableTemplates.length - 1 && index !== 0)
        //    setSelectedTemplateIndex((prev) => prev - 1);
    };

    const handleMoveGallery = (at, from, to) => {
        const [_, __, roomId] = at;
        const templates = structuredClone(usableTemplates);
        const template = templates?.find((el) => el.id === selectedTemplateId);
        if (roomId) {
            const room = template.rooms.find((room) => room.id === roomId);
            room.gallery = arrayMove(room.gallery, from, to);
        } else template.gallery = arrayMove(template.gallery, from, to);
        setUsableTemplates(templates);
    };

    const handleAddRoom = () => {
        const templates = structuredClone(usableTemplates);
        const template = templates?.find((el) => el.id === selectedTemplateId);
        const newRooms = template.rooms || [];
        newRooms.push({
            name: '',
            description: '',
            pax: 1,
            gallery: [],
            id: newId()
        });
        template.rooms = newRooms;
        setUsableTemplates(templates);
    };

    const handleRemoveRoom = (_, __, id) => {
        const confirm = window.confirm(_t.global.confirm());
        if (!confirm) return;
        if (selectedRoomId === id) setSelectedRoomId(null);

        const templates = structuredClone(usableTemplates);
        const template = templates?.find((el) => el.id === selectedTemplateId);
        const index = template.rooms.findIndex((room) => room.id === id);
        template.rooms.splice(index, 1);
        setUsableTemplates(templates);
    };

    const {
        meta: { data: userData }
    } = useAuth();

    if (isLoading)
        return (
            <LoadingSkeleton className={cn('flex items-center justify-center h-screen')}>
                <LoadingIndicator />
            </LoadingSkeleton>
        );
    return (
        <>
            <div className="fixed w-full h-[116px] bg-white top-0 left-0 z-50 shadow-sm">
                <ManageControls.BarSlot>
                    <p className="text-2xl">{_t.edit.templatesText()}</p>
                </ManageControls.BarSlot>
                <ManageControls.ButtonSlot>
                    <DetailButtons
                        save={{
                            busy: addTemplate.isLoading || updateTemplate.isLoading,
                            onClick: handleSaveTemplate,
                            disabled: selectedTemplate?.isBlocked && !userData.mainUser
                        }}
                        search={false}
                        clone={false}
                    ></DetailButtons>
                </ManageControls.ButtonSlot>
            </div>
            <div className="w-full md:h-full flex flex-col md:flex-row pt-[117px] border-none transition-all">
                <div className="min-w-[350px] w-full md:max-w-[50%] md:h-full p-3 gap-2 flex flex-col flex-1 overflow-auto bg-neutral-100">
                    <Input.Full
                        value={searchValue}
                        placeholder={_t.edit.templatesSearchPlaceholder()}
                        onChange={(e) => setSearchValue(e.target.value)}
                        // label={_t.edit.fields.filterTemplate()}
                    />
                    <ul className="flex flex-col w-full h-full mb-2 overflow-y-auto border divide-y rounded divide-neutral-200 border-neutral-200">
                        {filteredTemplates?.map((template, tIndex) => {
                            const selected = template.id === selectedTemplateId;
                            const { name: templateName, ...templateProps } = template; // HACK tolgo il name per non passarlo come props
                            return (
                                <PlanRow
                                    key={templateProps.id}
                                    selfId={templateProps.id}
                                    selected={selected}
                                    onSelect={() => setSelectedTemplateId(template.id)}
                                    onDelete={() => {
                                        if (templateProps.isBlocked && !userData.mainUser)
                                            return toast.error(_t.global.missingPermissions());
                                        handleDelete(Object.assign(template, { name: templateName }), tIndex);
                                    }}
                                    style={{ zIndex: 0 }}
                                    name={templateProps?.title ?? '-'}
                                    onClone={() =>
                                        setUsableTemplates((prev) => {
                                            const templates = structuredClone(prev);
                                            const newTemplate = { ...template, id: newId() };
                                            templates.push(newTemplate);
                                            setSelectedTemplateId(newTemplate.id);
                                            return templates;
                                        })
                                    }
                                    {...templateProps}
                                />
                            );
                        })}
                    </ul>
                    <AddRowButton onClick={() => setShowPopUp(true)}>{_t.global.new()}</AddRowButton>
                    <PopUp
                        types={popUpOptions}
                        isOpen={showPopUp}
                        onClose={() => setShowPopUp(false)}
                        addPlan={(type) => {
                            addNewTemplate(type);
                            setShowPopUp(false);
                        }}
                    />
                </div>
                <div className="flex-1 w-full h-full max-h-full pt-2 overflow-auto bg-white border-t md:w-1/2 md:border-t-0 border-neutral-300 md:pt-0">
                    {selectedTemplate ? (
                        <PlanDetail
                            {...selectedTemplate}
                            key={selectedTemplate.id}
                            onEdit={() => ''}
                            onChange={handleChange}
                            onAdd={handleAddRoom}
                            onDelete={handleRemoveRoom}
                            setSelectedRoomId={setSelectedRoomId}
                            selectedRoomId={selectedRoomId}
                            onMoveGallery={handleMoveGallery}
                            isTemplate={true}
                            transferTypes={transferTypes}
                        />
                    ) : (
                        <section className="relative items-center justify-center hidden md:flex md:h-full bg-neutral-50">
                            <p className="w-2/3 italic text-center text-neutral-400">{_t.edit.detailText()}</p>
                        </section>
                    )}
                </div>
            </div>
        </>
    );
}
